import { useContext, useEffect, useRef, useState } from 'react';
import { SiteSettingsContext } from '@namespace/cms';
import { USER_ROLES } from '@namespace/user';
import { UserContext } from '@namespace/user/src/context';
import { getZendeskClassicChatUser } from '../../api/getZendeskChatUser';
import { useZendeskAuthToken } from './hooks/useZendeskAuthToken';

export const useZendesk = () => {
  const [settings = {}] = useContext(SiteSettingsContext);
  const { supportSettings = {} } = settings || {};
  const { zendeskChat = {} } = supportSettings;
  const { key, isClassic } = zendeskChat;
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const isScriptLoading = useRef(false);
  const jwt = useZendeskAuthToken({ condition: isScriptLoaded && !isClassic });
  const [{ role }] = useContext(UserContext);

  const isUser = role === USER_ROLES.USER;

  const messangerType = isClassic ? 'webWidget' : 'messenger';

  const loadZendeskScript = () => {
    return new Promise((resolve, reject) => {
      if (isScriptLoading.current) {
        return;
      }

      if (isScriptLoaded) {
        resolve();
        return;
      }

      isScriptLoading.current = true;

      if (isClassic) {
        window.zESettings = {
          webWidget: {
            chat: {
              connectOnPageLoad: false
            },
            ...(isUser
              ? {
                  authenticate: {
                    chat: {
                      jwtFn(callback) {
                        getZendeskClassicChatUser()
                          .then((classicJwt) => {
                            callback(classicJwt?.data?.jwt);
                          })
                          .catch((e) => console.error(e));
                      }
                    }
                  }
                }
              : {})
          }
        };
      }

      const script = document.createElement('script');
      script.id = 'ze-snippet';
      script.src = `https://static.zdassets.com/ekr/snippet.js?key=${key}`;
      document.body.appendChild(script);

      script.addEventListener('load', () => {
        window.zE(messangerType, 'hide');
        window.zE(`${messangerType}:on`, 'close', () => {
          setTimeout(() => window.zE(messangerType, 'hide'), 1);
        });
        setIsScriptLoaded(true);
        isScriptLoading.current = false;
        setTimeout(() => resolve(true), 1);
      });

      script.addEventListener('error', () => {
        isScriptLoading.current = false;
        reject(new Error('Failed to load Zendesk script'));
      });
    });
  };

  window.ShowSupport = async () => {
    try {
      await loadZendeskScript();
      window.zE(messangerType, 'show');
      window.zE(messangerType, 'open');
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (window.zE && !isUser) {
      try {
        window.zE(messangerType, isClassic ? 'logout' : 'logoutUser');
      } catch (e) {
        console.warn(e);
      }
    }
  }, [isUser, messangerType, isClassic]);

  useEffect(() => {
    if (jwt && window.zE) {
      try {
        window.zE('messenger', 'loginUser', (callback) => {
          callback(jwt);
        });
      } catch (e) {
        console.warn(e);
      }
    }
  }, [jwt]);
};
